/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/FormA3.tsx.
 */

// src/pages/a3/FormA3.tsx
import React from "react";
import "./FormA3.css";
import A3Section from "./components/A3Section";
import A3EnergySummary from "./components/A3EnergySummary";
import A3CarbonSummary from "./components/A3CarbonSummary";
import { useA3FormData } from "./hooks/useA3FormData";
import Button from "../../../../../components/common/Button";

const FormA3: React.FC<{
  submissionId?: string;
  onNextStage: () => void;
}> = ({ submissionId, onNextStage }) => {
  const {
    formData,
    loading,
    error,
    errors,
    values,
    hasSavedOnce,
    isDirty,
    handleChange,
    patchValues,
    save,
    energyTotal,
    embodiedEnergyPerDeclaredFinishedMaterialMj,
    a3TotalEmbodiedCarbonKgCo2e,
    embodiedCarbonPerDeclaredFinishedMaterialKg,
    perFinishedEnergyUnitLabel,
    perFinishedCarbonUnitLabel,
    backgroundFinishedMaterialUnitSymbol,
    finishedMaterialId,
    manufacturingSpecificityFromBackground,
    a3SubmissionBackground,
    backgroundRawMaterialOptions,
    a3ProcessDetailsRowCount,
    a3FuelsPerProcess,
    addProcessDetailsRow,
    removeLastProcessDetailsRow,
    addFuelForProcess,
    removeFuelForProcess,
    a3ManufacturingProcessesCatalog,
    fuelDensityCatalog,
    fuelDensityUnitOptions,
    calorificValuesCatalog,
  } = useA3FormData(submissionId);

  if (loading) {
    return <div className="loading-text">Loading…</div>;
  }

  if (!formData || !formData.formSubSections) return <p>No form data available.</p>;

  return (
    <div className="formA3-container contributor-stage-form">
      <h2 className="form-heading">{formData.name}</h2>
      <p className="form-subtext">{formData.description}</p>

      {formData.formSubSections.map((section) => (
        <div key={section.code} className="form-subsection">
          <h3>{section.name}</h3>

          <A3Section
            section={section}
            values={values}
            materialId={finishedMaterialId ?? null}
            manufacturingSpecificityFromBackground={
              manufacturingSpecificityFromBackground
            }
            submissionBackground={a3SubmissionBackground}
            onChange={handleChange}
            patchValues={patchValues}
            backgroundRawMaterialOptions={backgroundRawMaterialOptions}
            processDetailsRowCount={a3ProcessDetailsRowCount}
            fuelsPerProcess={a3FuelsPerProcess}
            onAddProcessDetailsRow={addProcessDetailsRow}
            onRemoveLastProcessDetailsRow={removeLastProcessDetailsRow}
            onAddFuelForProcess={addFuelForProcess}
            onRemoveFuelForProcess={removeFuelForProcess}
            formData={formData}
            manufacturingProcessesCatalog={a3ManufacturingProcessesCatalog}
            fuelDensityCatalog={fuelDensityCatalog}
            fuelDensityUnitOptions={fuelDensityUnitOptions}
            calorificValuesCatalog={calorificValuesCatalog}
            backgroundFinishedMaterialUnitSymbol={
              backgroundFinishedMaterialUnitSymbol
            }
            errors={errors}
          />

          {section.code === "A3_EMBODIED_ENERGY_DETAILS" && (
            <A3EnergySummary
              energyMj={energyTotal}
              perDeclaredFinishedMj={
                embodiedEnergyPerDeclaredFinishedMaterialMj
              }
              perFinishedUnitLabel={perFinishedEnergyUnitLabel}
            />
          )}

          {section.code === "A3_EMBODIED_CARBON_DETAILS" && (
            <A3CarbonSummary
              totalEmbodiedCarbonKg={a3TotalEmbodiedCarbonKgCo2e}
              perDeclaredFinishedKg={embodiedCarbonPerDeclaredFinishedMaterialKg}
              perFinishedUnitLabel={perFinishedCarbonUnitLabel}
            />
          )}
        </div>
      ))}

      <div className="button-row">
        <Button label="Save" onClick={save} type="button" disabled={loading || !isDirty} />
        <Button
          label="Next"
          type="button"
          disabled={loading}
          onClick={async () => {
            if (loading) return;
            try {
              if (isDirty || !hasSavedOnce) {
                const result = await save();
                if (!result.success) return;
              }
            } catch {
              return;
            }
            onNextStage();
          }}
        />
      </div>
    </div>
  );
};

/** React component — Form A3. */
export default FormA3;